/* ============================================================
   KYNETIK PARTICLES — global collision effect
   - 20px neon pairs on random intervals (8–25s)
   - 1 pair 45% / 2 pairs 20% / 3 pairs 10% / skip 25%
   - Collision point anywhere in the viewport, occasionally up to
     40% outside its bounds
   - Two canvas layers: some pairs paint BEHIND page content,
     others paint ABOVE (chosen randomly per pair)
   - Elastic 2D collision, motion trail, impact sparkles
   ============================================================ */
(function () {
  'use strict';

  /* Run once, respect accessibility + old browsers */
  if (window.__kynParticles) return;
  window.__kynParticles = true;
  if (!('requestAnimationFrame' in window))   return;
  if (!document.createElement('canvas').getContext) return;
  try {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  } catch (_) {}

  /* ── Config ── */
  var NEON_R = 185, NEON_G = 255, NEON_B = 0;          /* #b9ff00 */
  var PARTICLE_RADIUS = 6;     /* core body — 12 px diameter */
  var TRAIL_LEN       = 70;    /* ≈1.15 s at 60 FPS — visibly long tail */
  var SPARK_LIFE_MS   = 450;
  var INTERVAL_MIN_MS = 8000;
  var INTERVAL_MAX_MS = 25000;
  var ABOVE_BIAS      = 0.8;   /* 80% of pairs paint ABOVE page content */

  /* ── Canvases ── */
  function makeCanvas(id, z) {
    var c = document.createElement('canvas');
    c.id = id;
    c.setAttribute('aria-hidden', 'true');
    c.style.cssText =
      'position:fixed;inset:0;top:0;left:0;width:100%;height:100%;' +
      'pointer-events:none;z-index:' + z + ';';
    return c;
  }
  var belowC = makeCanvas('knParticlesBelow', 0);     /* behind content */
  var aboveC = makeCanvas('knParticlesAbove', 9999);  /* above content (below drawer z:10000) */

  function insertCanvases() {
    if (!document.body) return;
    /* Below layer: first child so the page paints over it */
    document.body.insertBefore(belowC, document.body.firstChild);
    /* Above layer: last child so it paints over everything */
    document.body.appendChild(aboveC);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', insertCanvases);
  } else {
    insertCanvases();
  }

  var belowCtx = belowC.getContext('2d');
  var aboveCtx = aboveC.getContext('2d');
  var dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));

  function resize() {
    var w = window.innerWidth, h = window.innerHeight;
    [belowC, aboveC].forEach(function (c) {
      c.width  = Math.floor(w * dpr);
      c.height = Math.floor(h * dpr);
      c.style.width  = w + 'px';
      c.style.height = h + 'px';
      c.getContext('2d').setTransform(dpr, 0, 0, dpr, 0, 0);
    });
  }
  resize();
  window.addEventListener('resize', resize, { passive: true });

  /* ── Particle ── */
  function Particle(x, y, vx, vy, layer) {
    this.x = x; this.y = y;
    this.vx = vx; this.vy = vy;
    this.r = PARTICLE_RADIUS;
    this.layer = layer;
    this.trail = [];
    this.alive = true;
    this.alpha = 0;
    this.hasCollided = false;
    this.partner = null;
    this.spawnTime = performance.now();
    this.liveAfterCollideMs = 2600 + Math.random() * 1200; /* stay visible briefly post-bounce */
  }
  Particle.prototype.update = function (dt) {
    /* Fade in first ~200ms */
    this.alpha = Math.min(1, this.alpha + dt / 220);
    /* Integrate */
    this.x += this.vx * dt;
    this.y += this.vy * dt;
    /* Trail — record every other frame for smoother look */
    this.trail.unshift({ x: this.x, y: this.y });
    if (this.trail.length > TRAIL_LEN) this.trail.pop();
    /* Post-collision expiry: fade out then die */
    if (this.hasCollided) {
      var age = performance.now() - this.collideTime;
      if (age > this.liveAfterCollideMs) {
        this.alpha = Math.max(0, this.alpha - dt / 600);
        if (this.alpha <= 0) this.alive = false;
      }
    }
    /* Also die if very far off-screen */
    var pad = 200;
    if (this.x < -pad || this.x > window.innerWidth + pad ||
        this.y < -pad || this.y > window.innerHeight + pad) {
      if (this.hasCollided) this.alive = false;
    }
  };
  Particle.prototype.draw = function (ctx) {
    var a = this.alpha;
    if (a <= 0) return;

    /* Trail — quadratic path through recent points with fade toward tail */
    if (this.trail.length > 2) {
      ctx.save();
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      for (var i = 1; i < this.trail.length - 1; i++) {
        var t = 1 - i / this.trail.length;
        ctx.strokeStyle = 'rgba(' + NEON_R + ',' + NEON_G + ',' + NEON_B + ',' + (0.35 * t * a).toFixed(3) + ')';
        ctx.lineWidth = 2 * t + 0.4;
        ctx.beginPath();
        ctx.moveTo(this.trail[i - 1].x, this.trail[i - 1].y);
        ctx.quadraticCurveTo(
          this.trail[i].x, this.trail[i].y,
          (this.trail[i].x + this.trail[i + 1].x) / 2,
          (this.trail[i].y + this.trail[i + 1].y) / 2
        );
        ctx.stroke();
      }
      ctx.restore();
    }

    /* Outer halo — tighter radius, softer peak alpha */
    var halo = ctx.createRadialGradient(this.x, this.y, this.r * 0.5, this.x, this.y, this.r * 2.3);
    halo.addColorStop(0, 'rgba(' + NEON_R + ',' + NEON_G + ',' + NEON_B + ',' + (0.24 * a) + ')');
    halo.addColorStop(1, 'rgba(' + NEON_R + ',' + NEON_G + ',' + NEON_B + ',0)');
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.r * 2.3, 0, Math.PI * 2);
    ctx.fill();

    /* Body with 3D radial gradient */
    var body = ctx.createRadialGradient(
      this.x - this.r * 0.35, this.y - this.r * 0.4, this.r * 0.1,
      this.x, this.y, this.r
    );
    body.addColorStop(0,    'rgba(255,255,255,' + a + ')');
    body.addColorStop(0.25, 'rgba(224,255,120,' + a + ')');
    body.addColorStop(0.65, 'rgba(' + NEON_R + ',' + NEON_G + ',' + NEON_B + ',' + a + ')');
    body.addColorStop(1,    'rgba(85,120,0,'   + a + ')');
    ctx.fillStyle = body;
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.r, 0, Math.PI * 2);
    ctx.fill();

    /* Specular highlight */
    var spec = ctx.createRadialGradient(
      this.x - this.r * 0.45, this.y - this.r * 0.5, 0,
      this.x - this.r * 0.45, this.y - this.r * 0.5, this.r * 0.45
    );
    spec.addColorStop(0, 'rgba(255,255,255,' + (0.55 * a) + ')');
    spec.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = spec;
    ctx.beginPath();
    ctx.arc(this.x - this.r * 0.4, this.y - this.r * 0.45, this.r * 0.45, 0, Math.PI * 2);
    ctx.fill();
  };

  /* ── Sparkle (short-lived impact particle) ── */
  function Sparkle(x, y, vx, vy) {
    this.x = x; this.y = y;
    this.vx = vx; this.vy = vy;
    this.r = 1 + Math.random() * 2.6;
    this.life = 1;
    this.alive = true;
  }
  Sparkle.prototype.update = function (dt) {
    this.x += this.vx * dt;
    this.y += this.vy * dt;
    this.vx *= 0.955;
    this.vy *= 0.955;
    this.life -= dt / SPARK_LIFE_MS;
    if (this.life <= 0) this.alive = false;
  };
  Sparkle.prototype.draw = function (ctx) {
    var a = Math.max(0, this.life);
    if (a <= 0) return;
    /* Glow */
    var g = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.r * 3);
    g.addColorStop(0, 'rgba(' + NEON_R + ',' + NEON_G + ',' + NEON_B + ',' + (a * 0.8) + ')');
    g.addColorStop(1, 'rgba(' + NEON_R + ',' + NEON_G + ',' + NEON_B + ',0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.r * 3, 0, Math.PI * 2);
    ctx.fill();
    /* Core */
    ctx.fillStyle = 'rgba(255,255,255,' + a + ')';
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.r * a, 0, Math.PI * 2);
    ctx.fill();
  };

  /* ── Simulation state ── */
  var particles = [];
  var sparkles  = [];

  /* Spawn a single pair on a randomly chosen layer.
     Guaranteed-collision geometry: both particles travel on PARALLEL paths
     that are at most (a.r + b.r - ε) apart, so their bounding circles must
     intersect when they cross. A non-zero "impact parameter" offset keeps
     the bounce off-axis, giving a natural deflection instead of a flat 180°. */
  function spawnPair() {
    var w = window.innerWidth, h = window.innerHeight;
    /* Collision point: usually inside, occasionally up to 40% past an edge */
    var cx, cy;
    if (Math.random() < 0.72) {
      cx = Math.random() * w;
      cy = Math.random() * h;
    } else {
      cx = (Math.random() * 1.8 - 0.4) * w;
      cy = (Math.random() * 1.8 - 0.4) * h;
    }

    /* Axis of approach (radians) */
    var angle = Math.random() * Math.PI * 2;
    var cosA = Math.cos(angle);
    var sinA = Math.sin(angle);
    /* Perpendicular to the approach axis */
    var perpX = -sinA;
    var perpY =  cosA;

    /* Placement distance from the meeting region (large enough to stream in from off-screen) */
    var d     = Math.max(w, h) * (0.55 + Math.random() * 0.25);
    /* Approach speed (px/ms) */
    var speed = 0.14 + Math.random() * 0.12;

    /* Impact parameter: perpendicular offset between the two parallel paths.
       Must be < 2 × radius for the circles to overlap and collide.
       Range 0.2 r – 1.6 r → always collides, always off-axis. */
    var impact = PARTICLE_RADIUS * (0.2 + Math.random() * 1.4);
    if (Math.random() < 0.5) impact = -impact;

    var layer = Math.random() < ABOVE_BIAS ? 'above' : 'below';

    /* Particle A: start d away on one side, offset perpendicular by impact */
    var ax = cx - d * cosA + impact * perpX;
    var ay = cy - d * sinA + impact * perpY;
    /* Particle B: start d away on the other side, directly on the axis */
    var bx = cx + d * cosA;
    var by = cy + d * sinA;

    var a = new Particle(ax, ay,  speed * cosA,  speed * sinA, layer);
    var b = new Particle(bx, by, -speed * cosA, -speed * sinA, layer);
    a.partner = b;
    b.partner = a;
    particles.push(a, b);
  }

  /* Spawn event — weighted pair count + occasional skip */
  function spawnEvent() {
    var r = Math.random();
    var n;
    if      (r < 0.25) n = 0;   /* skip — quiet beat */
    else if (r < 0.70) n = 1;   /* 45% */
    else if (r < 0.90) n = 2;   /* 20% */
    else               n = 3;   /* 10% */
    for (var i = 0; i < n; i++) spawnPair();
  }

  function scheduleNext() {
    var delay = INTERVAL_MIN_MS + Math.random() * (INTERVAL_MAX_MS - INTERVAL_MIN_MS);
    setTimeout(function () {
      if (document.visibilityState !== 'hidden') spawnEvent();
      scheduleNext();
    }, delay);
  }

  /* ── Elastic collision between paired particles ── */
  function checkCollision(a, b) {
    if (a.hasCollided || b.hasCollided) return;
    var dx = b.x - a.x;
    var dy = b.y - a.y;
    var d2 = dx * dx + dy * dy;
    var sumR = a.r + b.r;
    if (d2 > sumR * sumR) return;

    var dist = Math.sqrt(d2) || 0.0001;
    var nx = dx / dist, ny = dy / dist;

    /* Relative velocity component along the normal */
    var vRelN = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
    if (vRelN > 0) return;   /* already separating */

    /* Equal-mass elastic impulse */
    var j = vRelN;
    a.vx += nx * j;  a.vy += ny * j;
    b.vx -= nx * j;  b.vy -= ny * j;

    /* Separate overlap to avoid sticking */
    var overlap = sumR - dist;
    a.x -= nx * overlap / 2;  a.y -= ny * overlap / 2;
    b.x += nx * overlap / 2;  b.y += ny * overlap / 2;

    a.hasCollided = true;  b.hasCollided = true;
    a.collideTime = b.collideTime = performance.now();

    /* Sparkles at contact point */
    var mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
    var impact = Math.min(1, Math.abs(vRelN) / 0.3);
    var n = 10 + Math.floor(Math.random() * 8 + impact * 8);
    for (var i = 0; i < n; i++) {
      var sa = Math.random() * Math.PI * 2;
      var ss = (0.15 + Math.random() * 0.35) * (0.6 + impact * 0.8);
      sparkles.push(new Sparkle(mx, my, Math.cos(sa) * ss, Math.sin(sa) * ss));
    }
  }

  /* ── Main loop ── */
  var lastT = performance.now();
  function loop(now) {
    var dt = Math.min(40, now - lastT); /* clamp for tab switches */
    lastT = now;

    /* Physics + lifecycle */
    for (var i = 0; i < particles.length; i++) particles[i].update(dt);
    for (var j = 0; j < particles.length; j++) {
      var p = particles[j];
      if (p.partner && !p.hasCollided) checkCollision(p, p.partner);
    }
    for (var k = particles.length - 1; k >= 0; k--) {
      if (!particles[k].alive) particles.splice(k, 1);
    }
    for (var s = 0; s < sparkles.length; s++) sparkles[s].update(dt);
    for (var t = sparkles.length - 1; t >= 0; t--) {
      if (!sparkles[t].alive) sparkles.splice(t, 1);
    }

    /* Clear both layers */
    var vw = window.innerWidth, vh = window.innerHeight;
    belowCtx.clearRect(0, 0, vw, vh);
    aboveCtx.clearRect(0, 0, vw, vh);

    /* Draw particles onto their chosen layer */
    for (var d = 0; d < particles.length; d++) {
      var pp = particles[d];
      pp.draw(pp.layer === 'below' ? belowCtx : aboveCtx);
    }
    /* Sparkles always paint on the upper layer so impacts are visible */
    for (var e = 0; e < sparkles.length; e++) sparkles[e].draw(aboveCtx);

    requestAnimationFrame(loop);
  }

  /* ── Boot ── */
  function start() {
    /* First spawn after 3–6 s, then the regular 8–25 s cadence */
    setTimeout(function () {
      spawnEvent();
      scheduleNext();
    }, 3000 + Math.random() * 3000);
    requestAnimationFrame(function (t) { lastT = t; loop(t); });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
